import { CloseSquare, DocumentUpload } from "iconsax-react";
import { useState } from "react";
import PrimaryAddButton from "../../buttons/PrimaryAddButton";
import { toast } from "react-toastify";
import { getToken } from "../../../utils/token";
import instance from "../../../axios/instance";

export default function UploadPatientsCsvModal(props) {
  const [csvData, setCsvData] = useState([]);
  const [fileName, setFileName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState("");

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    setError("");

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      parseCSV(text);
    };
    reader.readAsText(file);
  };

  const parseCSV = (text) => {
    const lines = text.split("\n");
    if (lines.length < 2) {
      setError("CSV file is empty or missing headers");
      return;
    }

    // Normalize headers
    const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
    const required = [
      "id",
      "delivery_address",
      "delivery_city",
      "delivery_phone",
      "delivery_state",
      "zipcode",
    ];

    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      const values = line.split(",").map((v) => v.trim());

      // Map values to headers
      const row = {};
      headers.forEach((h, index) => {
        row[h] = values[index];
      });

      // Find patient
      const patient = props.patients.find((p) => p.email_address === row.email);

      data.push({
        ...row,
        isNewInValid: !patient && !row.email,
        isNew: !patient,
        id: patient ? patient.id : row.email,
        patientName: patient
          ? `${patient.lastname} ${patient.firstname}`
          : `${row.lastname} ${row.firstname}`,
        patientData: patient,
      });
    }
    setCsvData(data);
  };

  const handleCreateNewPatients = async (patients) => {
    const token = getToken();
    const newPatients = await instance.post(
      "/doctor/patient/multiple",
      { patients },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    return newPatients.data;
  };

  const handleConfirm = async () => {
    const newData = csvData.filter((d) => d.isNew);
    let newPatients = [];
    setLoading(true);
    try {
      if (newData.length >= 1) {
        newPatients = await handleCreateNewPatients(newData);
        await props.fetchPatients();
      }
      setLoading(false);
      const filteredNewPatients = newData
        .map((d) => {
          const patientData = newPatients.find(
            (p) => p.email_address === d.email
          );
          if (patientData) {
            return {
              ...d,
              id: patientData.id,
              patientName: `${patientData.lastname} ${patientData.firstname}`,
              patientData,
            };
          }
          return null;
        })
        .filter((d) => d);
      props.onConfirm([
        ...csvData.filter((d) => !d.isNew),
        ...filteredNewPatients,
      ]);
      handleClose();
    } catch (e) {
      setLoading(false);

      toast.error(
        error?.response?.data?.message || "Failed to create new patients"
      );
    }
  };

  const handleClose = () => {
    setCsvData([]);
    setFileName("");
    setError("");
    props.onClose();
  };

  const validCount = csvData.length;
  const newCount = csvData.filter((d) => d.isNew).length;

  return (
    <div
      className={`${
        props.isOpen ? "flex" : "hidden"
      } fixed inset-0 z-50 items-center justify-center bg-black bg-opacity-50`}
    >
      <div className="bg-white rounded-lg w-[800px] max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-5 border-b">
          <h2 className="text-lg font-bold">Upload Patients CSV</h2>
          <button onClick={handleClose}>
            <CloseSquare
              size="24"
              className="text-gray-500 hover:text-gray-700"
            />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 flex flex-col items-center justify-center mb-6">
            <DocumentUpload size="48" className="text-primary mb-2" />
            <p className="text-sm text-gray-500 mb-4">
              Upload your CSV file (email, firstname, lastname,
              delivery_address, delivery_city, delivery_phone, delivery_state,
              zipcode)
            </p>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
              id="csv-upload"
            />
            <label
              htmlFor="csv-upload"
              className="px-4 py-2 bg-primary text-white rounded cursor-pointer hover:bg-opacity-90 transition-all"
            >
              Select File
            </label>
            {fileName && (
              <p className="mt-2 text-sm text-gray-700">{fileName}</p>
            )}
            {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
          </div>

          {csvData.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 text-gray-700 font-medium">
                  <tr>
                    <th className="px-4 py-2">Email</th>
                    <th className="px-4 py-2">Patient Name</th>
                    <th className="px-4 py-2">Address</th>
                    <th className="px-4 py-2">Phone</th>
                    <th className="px-4 py-2">City</th>
                    <th className="px-4 py-2">State</th>
                    <th className="px-4 py-2">Zip</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {csvData.map((row, idx) => (
                    <tr key={idx} className={!row.isNew ? "bg-green-50" : ""}>
                      <td className="px-4 py-2">{row.email}</td>
                      <td
                        className={`px-4 py-2 ${
                          row.isNew ? "text-green-600 font-medium" : ""
                        }`}
                      >
                        {row.patientName}
                      </td>
                      <td className="px-4 py-2">{row.delivery_address}</td>
                      <td className="px-4 py-2">{row.delivery_phone}</td>
                      <td className="px-4 py-2">{row.delivery_city}</td>
                      <td className="px-4 py-2">{row.delivery_state}</td>
                      <td className="px-4 py-2">{row.zipcode}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="p-5 border-t flex justify-between items-center bg-gray-50 rounded-b-lg">
          <div className="text-sm text-gray-600">
            {csvData.length > 0 && (
              <span>
                Found {newCount} new patients out of {csvData.length} rows.
              </span>
            )}
          </div>
          <PrimaryAddButton
            title={`Add ${validCount} Patients`}
            onClick={handleConfirm}
            disabled={validCount === 0}
            color="white"
            isLoading={loading}
          />
        </div>
      </div>
    </div>
  );
}
